<template>
  <v-dialog v-model="show" max-width="900px" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center pa-6">
        <v-icon class="mr-3" size="28" color="primary">{{
          originalPattern && originalPattern.trim() ? 'mdi-compare' : 'mdi-robot'
        }}</v-icon>
        <span class="text-h6">{{
          originalPattern && originalPattern.trim() ? 'Pattern Comparison' : 'AI Generated Pattern'
        }}</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-6">
        <v-row class="mb-6">
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-icon small class="mr-2" color="success">mdi-chart-line</v-icon>
              <span class="font-weight-medium">Confidence Score:</span>
              <span class="ml-2 text-h6 font-weight-bold"
                >{{ Math.round(confidenceScore * 100) }}%</span
              >
              <v-chip small :color="confidenceColor" text-color="white" class="ml-3">{{
                confidenceLabel
              }}</v-chip>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="originalPattern && originalPattern.trim()" class="mb-6">
          <v-col cols="6">
            <div class="d-flex align-center mb-3">
              <v-icon small class="mr-2" color="grey darken-1">mdi-file-document</v-icon>
              <span class="font-weight-medium text-subtitle2">Current Pattern</span>
            </div>
            <v-textarea
              :value="originalPattern"
              readonly
              outlined
              dense
              rows="5"
              class="pattern-textarea"
            />
          </v-col>
          <v-col cols="6">
            <div class="d-flex align-center mb-3">
              <v-icon small class="mr-2" color="primary">mdi-robot</v-icon>
              <span class="font-weight-medium text-subtitle2">AI Generated Pattern</span>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon small class="ml-2" color="info" v-bind="attrs" v-on="on"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <span
                  >Using the AI pattern will overwrite your current pattern. This action cannot be
                  undone.</span
                >
              </v-tooltip>
            </div>
            <v-textarea
              v-model="editablePattern"
              outlined
              dense
              rows="5"
              class="pattern-textarea ai-pattern"
              @blur="onPatternBlur"
            />
          </v-col>
        </v-row>
        <v-row v-else class="mb-6">
          <v-col cols="12">
            <div class="d-flex align-center mb-3">
              <v-icon small class="mr-2" color="primary">mdi-robot</v-icon>
              <span class="font-weight-medium text-subtitle2">AI Generated Pattern</span>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon small class="ml-2" color="info" v-bind="attrs" v-on="on"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <span
                  >Using the AI pattern will overwrite your current pattern. This action cannot be
                  undone.</span
                >
              </v-tooltip>
            </div>
            <v-textarea
              v-model="editablePattern"
              outlined
              dense
              rows="5"
              class="pattern-textarea ai-pattern"
              @blur="onPatternBlur"
            />
          </v-col>
        </v-row>

        <!-- Discovery Matching Results -->
        <v-divider class="my-4"></v-divider>
        <div class="mb-4">
          <div
            class="d-flex align-center mb-4"
            style="cursor: pointer;"
            @click="resultsExpanded = !resultsExpanded"
          >
            <v-icon small class="mr-2" color="info">mdi-database-search</v-icon>
            <span class="font-weight-medium text-subtitle2">Matching Results</span>
            <v-chip small color="info" text-color="white" class="ml-3">
              {{ matchCount }} match{{ matchCount !== 1 ? 'es' : '' }}
            </v-chip>
            <v-spacer></v-spacer>
            <v-icon small class="ml-auto">{{
              resultsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
            }}</v-icon>
          </div>
          <div v-show="resultsExpanded" class="results-container">
            <discovery-matching
              v-if="show"
              ref="discoveryMatching"
              :domain="domain"
              :pattern="editablePattern"
              :product="product"
              @match-count-updated="matchCount = $event"
            />
          </div>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-6">
        <v-btn
          v-if="originalPattern && originalPattern.trim()"
          color="success"
          class="ml-2"
          @click="$emit('keep-original')"
        >
          <v-icon left small>mdi-restore</v-icon>
          Keep Original
        </v-btn>
        <v-btn class="ml-2 ai-action-btn" @click="$emit('use-ai-pattern')">
          <v-icon left small>mdi-robot</v-icon>
          Use AI Pattern
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn class="cancel-btn" @click="$emit('cancel')">
          <v-icon left small>mdi-close</v-icon>
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DiscoveryMatching from '@/components/cls/DiscoveryMatching';
import HTTP from '@/api/ant';

export default {
  name: 'PatternComparisonDialog',
  components: { DiscoveryMatching },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    originalPattern: {
      type: String,
      default: '',
    },
    aiGeneratedPattern: {
      type: String,
      default: '',
    },
    confidenceScore: {
      type: Number,
      default: 0,
    },
    product: {
      type: String,
      default: 'video',
    },
    domain: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      editablePattern: '',
      matchCount: 0,
      resultsExpanded: false,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    confidenceColor() {
      if (this.confidenceScore >= 0.8) return 'success';
      if (this.confidenceScore >= 0.6) return 'warning';
      return 'error';
    },
    confidenceLabel() {
      if (this.confidenceScore >= 0.8) return 'High';
      if (this.confidenceScore >= 0.6) return 'Medium';
      return 'Low';
    },
  },
  watch: {
    aiGeneratedPattern: {
      handler(newPattern) {
        this.editablePattern = newPattern;
        this.$nextTick(() => {
          this.$refs.discoveryMatching?.testPattern();
        });
      },
    },
    editablePattern: {
      handler() {
        this.$nextTick(() => {
          this.$refs.discoveryMatching?.testPattern();
          this.updateMatchCount();
        });
      },
    },
    show: {
      handler(isOpen) {
        if (isOpen && this.editablePattern) {
          this.$nextTick(() => {
            this.$refs.discoveryMatching?.testPattern();
          });
        }
      },
    },
  },
  methods: {
    fetchLockedDomain() {
      HTTP.get('/video/domains', {
        params: { filter: { status: 'locked' } },
      })
        .then(({ data }) => {
          if (data.length > 0) {
            this.$emit('update:domain', data[0].domain);
          }
        })
        .catch(err => {
          console.error('Error fetching locked domain:', err);
        });
    },
    onPatternBlur() {
      this.$refs.discoveryMatching?.testPattern();
      this.$nextTick(() => {
        this.updateMatchCount();
      });
    },
    updateMatchCount() {
      this.matchCount = this.$refs.discoveryMatching?.totalItems || 0;
    },
  },
};
</script>

<style scoped>
.pattern-textarea {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.ai-pattern {
  border-color: #667eea !important;
}

.results-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
}

.ai-action-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;
}

.ai-action-btn:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
  box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
  transform: translateY(-1px);
}

.cancel-btn {
  color: #999 !important;
  transition: all 0.2s ease !important;
}

.cancel-btn:hover {
  color: #ef5350 !important;
}
</style>
